import type { IAuthUser } from '@kfolks/types'
import { AuthErrorCode } from '@kfolks/types'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { isAxiosError } from 'axios'
import { createContext, useCallback, useEffect, useMemo, type ReactNode } from 'react'
import { authService } from '@/services/auth.service'

export const AUTH_QUERY_KEY = ['auth', 'me'] as const

export interface AuthContextValue {
	user: IAuthUser | null
	isAuthenticated: boolean
	isLoading: boolean
	login: () => void
	logout: () => void
}

export const AuthContext = createContext<AuthContextValue | null>(null)

/**
 * Mirrors the error-handling rules locked in by `auth-context-provider`'s
 * spec: ACCOUNT_INACTIVE redirects to /error/inactive; a bare 401 redirects
 * to /error/unauthorized. The provider MUST NOT touch browser storage —
 * the access token lives in an HttpOnly cookie that JS cannot read or
 * clear; the server clears it on logout / on 401-from-an-expired-cookie
 * a subsequent request will land at /auth/login again.
 *
 * Network or 5xx errors fall through (no redirect) so transient backend
 * failures don't sign the user out.
 */
function handleAuthQueryError(error: unknown): void {
	if (!isAxiosError(error)) return

	const status = error.response?.status
	const errBody = error.response?.data as { error?: { code?: string } } | undefined
	const errorCode = errBody?.error?.code

	// Don't re-navigate if we're already on the destination — `window.location.href`
	// to the current URL reloads the page in most browsers, which would re-mount
	// AuthProvider, re-fire /auth/me, hit the same 401, and loop indefinitely.
	const path = window.location.pathname

	if (errorCode === AuthErrorCode.ACCOUNT_INACTIVE) {
		if (path !== '/error/inactive') window.location.href = '/error/inactive'
		return
	}

	if (status === 401) {
		if (path !== '/error/unauthorized' && path !== '/login' && path !== '/auth/callback') {
			window.location.href = '/error/unauthorized'
		}
	}
}

interface AuthProviderProps {
	children: ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
	const queryClient = useQueryClient()

	// /auth/me fires on mount everywhere EXCEPT `/auth/callback`. On the
	// callback route the cookie isn't set yet — the page itself runs
	// `POST /auth/callback` (which sets the cookie) and then seeds this
	// query's cache via `queryClient.setQueryData(AUTH_QUERY_KEY, …)`. Firing
	// `/auth/me` here would deterministically 401 and, with
	// `staleTime: Infinity` + `retry: false`, leave the cache stuck in that
	// error state for the rest of the session.
	const onCallbackRoute = window.location.pathname === '/auth/callback'
	const { data, isLoading, isFetching, error } = useQuery<IAuthUser>({
		queryKey: [...AUTH_QUERY_KEY],
		queryFn: () => authService.getCurrentUser(),
		enabled: !onCallbackRoute,
		staleTime: Infinity,
		retry: false,
	})

	useEffect(() => {
		if (error) handleAuthQueryError(error)
	}, [error])

	const login = useCallback(() => authService.initiateEntraLogin(), [])

	const logout = useCallback(() => {
		// Clear the server-side session cookie first so the user isn't briefly
		// authenticated on whatever page Entra bounces them back to. Then drop
		// the cached viewer and let Entra terminate the IdP session. We don't
		// await here — the Entra navigation is the user-visible terminator;
		// the cookie clear is fire-and-forget (best-effort, server-side
		// Set-Cookie returns instantly anyway).
		void authService.clearServerSession().finally(() => {
			queryClient.removeQueries({ queryKey: AUTH_QUERY_KEY })
			authService.logout()
		})
	}, [queryClient])

	const user = data ?? null

	const value = useMemo<AuthContextValue>(
		() => ({
			user,
			isAuthenticated: user !== null,
			isLoading: (isLoading || isFetching) && !user && !error,
			login,
			logout,
		}),
		[user, isLoading, isFetching, error, login, logout],
	)

	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
